import { useMemo } from 'react';
import { createColumnHelper, useReactTable, getCoreRowModel } from '@tanstack/react-table';

import {
  adjustMinimalClassTimeAcrossTZ,
  GetCoachAttendanceRollupResult,
  StudentGroupAuditWithClassTimeLevel,
} from '@airreading/common/data';
import { formatPlainDate, FrequentTimeZones } from '@airreading/common';
import { formatStudentGrades } from '@/lib/student-group-utils.js';
import { CoachAttendanceAbsences } from './coach-attendance-absences.js';

import { EntityTable } from '@/components/table/entity-table.js';
import { InfoCard } from '@/components/info-card.js';
import { Badge } from '@/components/ui/badge.js';
import { PeopleBadgeList } from '../../scheduling/student-groups/-components/people-badge-list.js';
import { formatStudentClassTimeAbbr } from '../../scheduling/schedules/-components/formatting.js';
import { CountAsReassignedToggle } from './toggle-count-as-reassigned.js';
import { PaginationRequest, usePagination } from '@/lib/pagination.js';
import { RegisteredRouteTree, RoutesWithValidConstraint } from '@/lib/routing.js';
import { RouteById } from '@tanstack/react-router';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { EmptyArray, getCoachAttendanceRollupsForGroups, getCoachReassignedStudentGroups } from '@/lib/api-calls.js';
import { TableErrorState } from '@/components/table/table-error-state.js';
import { Check } from 'lucide-react';

const columnHelper = createColumnHelper<StudentGroupAuditWithClassTimeLevel>();
const getColumns = (attendanceMap: Record<string, GetCoachAttendanceRollupResult>) => [
  columnHelper.accessor((row) => row.studentGroup.classTime, {
    id: 'classTimes',
    header: 'Class times (CT)',
    enableSorting: false,
    cell: ({ getValue }) => {
      const classTime = getValue();
      return (
        <Badge variant="outline" className="bg-[#F1F1F1] font-normal">
          {formatStudentClassTimeAbbr(
            adjustMinimalClassTimeAcrossTZ(
              classTime,
              classTime.schedule.school.org.timeZone,
              FrequentTimeZones.UsCentral,
            ),
          )}
        </Badge>
      );
    },
  }),
  columnHelper.accessor('coachAssignedAtPlainDate', {
    id: 'startDate',
    header: 'Start date',
    enableSorting: false,
    cell: ({ getValue }) => {
      const value = getValue();
      return value ? formatPlainDate(value) : '-';
    },
  }),
  columnHelper.accessor('coachUnassignedAtPlainDate', {
    id: 'endDate',
    header: 'End date',
    enableSorting: false,
    cell: ({ getValue }) => {
      const value = getValue();
      return value ? formatPlainDate(value) : '-';
    },
  }),
  columnHelper.accessor((row) => row.studentGroup.students, {
    id: 'students',
    header: 'Students',
    enableSorting: false,
    cell: ({ getValue }) => {
      return <PeopleBadgeList people={getValue()} />;
    },
  }),
  columnHelper.accessor((row) => row.studentGroup.grades, {
    id: 'grades',
    header: 'Grades',
    enableSorting: false,
    cell: ({ getValue }) => formatStudentGrades(getValue()),
  }),
  columnHelper.accessor((row) => row.studentGroup.classTime?.schedule?.school?.name ?? '', {
    id: 'school',
    header: 'School',
    enableSorting: false,
    cell: ({ getValue }) => getValue(),
  }),
  columnHelper.accessor((row) => row.studentGroup.classTime?.schedule?.school?.org?.name ?? '', {
    id: 'district',
    header: 'District',
    enableSorting: false,
    cell: ({ getValue }) => getValue(),
  }),
  columnHelper.accessor((row) => row, {
    id: 'reassigned',
    header: 'Count as reassigned',
    enableSorting: false,
    cell: ({ getValue }) => <CountAsReassignedToggle studentGroupAudit={getValue()} />,
  }),
  columnHelper.accessor('droppedByTeacher', {
    id: 'droppedByTeacher',
    header: 'Dropped by teacher',
    enableSorting: false,
    cell: ({ getValue }) => (getValue() ? <Check /> : null),
  }),
  columnHelper.accessor((row) => row.studentGroup, {
    id: 'absences',
    header: 'Teacher absences',
    enableSorting: false,
    cell: ({ getValue }) => {
      const { id, maxPlannedAbsences, shortNoticeSubRequestLimit } = getValue();
      const coachAttendanceRollup = attendanceMap[id];
      return (
        <CoachAttendanceAbsences
          coachAttendanceRollup={coachAttendanceRollup}
          maxPlannedAbsences={maxPlannedAbsences}
          shortNoticeSubRequestLimit={shortNoticeSubRequestLimit}
        />
      );
    },
  }),
];

export function ReassignedStudentGroupsCard({
  coachId,
  route,
}: {
  coachId: string;
  route: RouteById<RegisteredRouteTree, RoutesWithValidConstraint<PaginationRequest>>;
}) {
  const { pageIndex, pageSize, onPaginationChange } = usePagination(route.id);
  const {
    data: response,
    error,
    refetch,
    isPending,
  } = useQuery(getCoachReassignedStudentGroups(coachId, { pageIndex, pageSize }));
  const reassignedStudentGroups = useMemo(() => response?.reassignedStudentGroups || EmptyArray, [response]);
  const totalRowCount = useMemo(() => response?.total || 0, [response]);

  const studentGroupIds = reassignedStudentGroups.map((c) => c.studentGroupId);
  const { data: attendanceMap = {} } = useSuspenseQuery(
    getCoachAttendanceRollupsForGroups({ coachId, studentGroupIds }),
  );

  const columns = useMemo(() => getColumns(attendanceMap), [attendanceMap]);

  const table = useReactTable({
    data: reassignedStudentGroups,
    columns,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    manualPagination: true,
    rowCount: totalRowCount,
  });

  const droppedByTeacherCount = reassignedStudentGroups.filter((c) => c.droppedByTeacher).length;

  return (
    <InfoCard
      headerText={
        <>
          Past groups{' '}
          <span className="text-sm font-normal text-gray-500 ml-2">(Dropped by teacher: {droppedByTeacherCount})</span>
        </>
      }
    >
      <EntityTable
        table={table}
        hasError={!!error}
        showPagination
        isLoading={isPending}
        errorState={<TableErrorState refetchAction={refetch} />}
        totalRowCount={totalRowCount}
        emptyStateClassName="translate-y-0"
      />
    </InfoCard>
  );
}
